import dayjs from "dayjs";
import partition from "lodash-es/partition";
import uniq from "lodash-es/uniq";

import type * as T from "@src/types";
import {
  getTrackingEndedMovementTime,
  isPositionTrackingEnded,
  isTrackingEndedFactChartEligible,
  resolvePositionExecutionInterval,
} from "@src/utils/positionStatus";
import { getCachedCandleBounds } from "./cacheData";

import { tvResolutionToApiPeriod } from "../datafeed/helpers";
import { createSymbolDatafeed } from "../datafeed/symbolDatafeed";

import * as indicators from "./indicators";
import * as panes from "./panes";
import * as shapes from "./shapes";

import type * as TV from "@charting_library";

// Re-export from helpers for use in TVAdvanced.tsx
export { tvResolutionToApiPeriod };

const maxResMap = {
  idx: 8,
  maxMinutes: 645120,
  apiPeriod: "1d",
  tvRes: "D" as T.TV.ResolutionString,
}; // 6 months

// Exported for use in resolution change handler
export const resolutionMap = [
  { idx: 0, maxMinutes: 1440, apiPeriod: "1m", tvRes: "1" },
  { idx: 1, maxMinutes: 2880, apiPeriod: "5m", tvRes: "5" },
  { idx: 2, maxMinutes: 4320, apiPeriod: "30m", tvRes: "30" },
  { idx: 3, maxMinutes: 15120, apiPeriod: "1h", tvRes: "60" },
  { idx: 4, maxMinutes: 30240, apiPeriod: "2h", tvRes: "120" },
  // { maxMinutes: 20160, tvRes: '240'},
  { idx: 5, maxMinutes: 60480, apiPeriod: "4h", tvRes: "240" },
  // { maxMinutes: 80640, tvRes: '360' },
  { idx: 6, maxMinutes: 120960, apiPeriod: "12h", tvRes: "720" },
  { idx: 7, maxMinutes: 322560, apiPeriod: "1d", tvRes: "D" },
  { ...maxResMap },
] as { idx: number; maxMinutes: number; apiPeriod: string; tvRes: T.TV.ResolutionString }[];

export const isNumericString = (str: string) => /^\d+$/.test(str);

/**
 * Get resolution in minutes for comparison purposes
 */
export const getResolutionMinutes = (res: string): number => {
  const map: Record<string, number> = {
    "1": 1,
    "3": 3,
    "5": 5,
    "15": 15,
    "30": 30,
    "60": 60,
    "120": 120,
    "240": 240,
    "720": 720,
    D: 1440,
    W: 10080,
  };
  const minutes = map[res];
  if (minutes === undefined) {
    console.warn(`[getResolutionMinutes] Unknown resolution "${res}", defaulting to 1 minute`);
    return 1;
  }
  return minutes;
};

/**
 * Convert API candles to typed arrays for datafeed consumption
 * Used when resolution changes and new candles are fetched from API
 */
export const convertApiCandlesToTypedArray = (
  candles: {
    open_time: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume?: number;
  }[],
): T.TypedArrayCandles => {
  if (!candles.length) {
    return {
      times: new Uint32Array(0),
      opens: new Float32Array(0),
      highs: new Float32Array(0),
      lows: new Float32Array(0),
      closes: new Float32Array(0),
      volumes: new Float32Array(0),
    };
  }

  // Parse timestamps once and sort descending (newest first) for binary search efficiency
  const withTimestamps = candles.map((c) => ({
    ...c,
    timestamp: Math.floor(new Date(c.open_time).getTime() / 1000),
  }));
  const sorted = withTimestamps.sort((a, b) => b.timestamp - a.timestamp);

  return {
    times: new Uint32Array(sorted.map((c) => c.timestamp)),
    opens: new Float32Array(sorted.map((c) => c.open)),
    highs: new Float32Array(sorted.map((c) => c.high)),
    lows: new Float32Array(sorted.map((c) => c.low)),
    closes: new Float32Array(sorted.map((c) => c.close)),
    volumes: new Float32Array(sorted.map((c) => c.volume || 0)),
  };
};

export const setWidgetOptions = (
  options: Partial<TV.ChartingLibraryWidgetOptions>,
  datafeedArgs: T.ArrayTypedPropsForDatafeed,
): { widgetOptions: TV.ChartingLibraryWidgetOptions; datafeed: T.ExternalDatafeed } => {
  const interval =
    resolutionMap.find((r) => r.apiPeriod === datafeedArgs?.api_period)?.tvRes ||
    ("30" as TV.ResolutionString);

  // Create a fresh datafeed instance for each widget (prevents state pollution)
  const symbolDatafeed = createSymbolDatafeed();

  // Inject the data into the datafeed state to consume candles, indicators, etc
  symbolDatafeed.props = {
    ...symbolDatafeed.props,
    ...datafeedArgs,
    // Copies, so the markers added below go in this datafeed's marks and not in the ones it was given
    marks: { opens: [...datafeedArgs.marks.opens], closes: [...datafeedArgs.marks.closes] },
  };

  // Store original candle time range for resolution change (fetch full range, not just viewport)
  if (datafeedArgs.candles?.times?.length > 0) {
    const times = datafeedArgs.candles.times;
    symbolDatafeed.state.originalTimeRange = {
      from: times[times.length - 1], // oldest (descending order)
      to: times[0], // newest
    };
  }

  // Enable API fallback mode (when cache is unavailable)
  // symbolInfo already contains exchange/symbol, resolution comes from getBars
  if (datafeedArgs.useApiFallback) {
    symbolDatafeed.state.useApiFallback = true;

    // Calculate earliest position time to avoid returning noData prematurely
    if (datafeedArgs.positions?.length) {
      const validTimes = datafeedArgs.positions
        .filter((position) => !isPositionTrackingEnded(position))
        .map((p) => Math.floor(new Date(p.start_ts).getTime() / 1000))
        .filter((t) => !isNaN(t) && isFinite(t));

      if (validTimes.length > 0) {
        symbolDatafeed.state.earliestPositionTime = Math.min(...validTimes);
      }
    }
  }

  // Set markers (positions already filtered by symbol in preparePropsForDatafeed)
  // Detect group view: multiple distinct execution_ids means group mode
  const distinctExecIds = new Set(
    symbolDatafeed.props?.positions.map((p: T.AppPosition) => p.execution_id).filter(Boolean),
  );
  const isGroupView = distinctExecIds.size > 1;

  const candleBounds = getCachedCandleBounds(datafeedArgs.candles, datafeedArgs.api_period);
  symbolDatafeed.state.trackingEndedFactBounds = candleBounds;
  symbolDatafeed.props?.positions.forEach((pos: T.AppPosition) => {
    const trackingEnded = isPositionTrackingEnded(pos);
    const executionInterval = trackingEnded
      ? resolvePositionExecutionInterval(pos, {
          start_ts: datafeedArgs.start_ts,
          end_ts: datafeedArgs.end_ts,
          executionBoundaries: datafeedArgs.executionBoundaries,
          group: isGroupView,
        })
      : null;
    pos.movements?.forEach((mov: T.ApiMovement) => {
      const movementTime = trackingEnded ? getTrackingEndedMovementTime(mov) : mov.movement_ts;
      if (
        trackingEnded &&
        !isTrackingEndedFactChartEligible(movementTime, candleBounds, executionInterval)
      )
        return;
      const marker = shapes.createMovementMark(
        pos,
        trackingEnded ? { ...mov, movement_ts: movementTime } : mov,
        isGroupView,
      );
      if ("text" in marker) symbolDatafeed.props.marks.opens.push(marker);
      else if ("tooltip" in marker) symbolDatafeed.props.marks.closes.push(marker);
    });
  });

  const widgetOptions: TV.ChartingLibraryWidgetOptions & {
    datafeed: T.ExternalDatafeed;
  } = {
    /* @ts-expect-error | TODO: type collision? */
    datafeed: symbolDatafeed,
    symbol: `${datafeedArgs?.symbol?.exchange}:${datafeedArgs?.symbol?.name}`,
    interval: interval,
    library_path: "/charting_library/",
    locale: "en",
    debug: false,
    charts_storage_url: "https://saveload.tradingview.com",
    charts_storage_api_version: "1.1" as TV.AvailableSaveloadVersions,
    client_id: "tradingview.com",
    user_id: "public_user_id",
    fullscreen: false,
    autosize: true,
    theme: "dark",
    custom_css_url: "custom.css", // lib/charting_library/custom.css — our overrides
    enabled_features: ["header_resolutions"],
    studies_overrides: {},
    custom_formatters: {
      studyFormatterFactory: (format) =>
        format.type === indicators.ZONE_LEGEND_FORMAT.type &&
        format.precision === indicators.ZONE_LEGEND_FORMAT.precision
          ? { format: indicators.formatZoneLegendValue }
          : null,
    },
    custom_indicators_getter: (PineJS) => {
      const indicatorsList = datafeedArgs?.indicators || [];

      // Detect band pairs for filled area rendering
      const bandPairs = indicators.detectBandPairs(indicatorsList);

      // Store band pairs in datafeed state for use in renderStudies
      symbolDatafeed.state.bandPairs = bandPairs;

      // Track which indicators are part of pairs (to avoid rendering them separately)
      const pairedIndicatorNames = new Set(
        bandPairs.flatMap((p) => [p.upperIndicator.name, p.lowerIndicator.name]),
      );

      // Generate band group indicators with filled areas
      let idx = 1;
      const bandGroupIndicators = bandPairs.map((pair) =>
        indicators.generateBandGroupIndicator(PineJS, pair, idx++),
      );

      // Generate single-line indicators for non-paired indicators (zones become background tints)
      const singleLineIndicators = indicatorsList
        .filter((ind) => !pairedIndicatorNames.has(ind.name))
        .map((ind) =>
          indicators.isZoneIndicator(ind)
            ? indicators.generateZoneIndicator(PineJS, ind, idx++)
            : indicators.generateLineIndicator(PineJS, ind, idx++, {
                isEditable: datafeedArgs?.editableIndicators,
              }),
        );

      // Combine both types
      const customIndicators = [...bandGroupIndicators, ...singleLineIndicators];

      return Promise.resolve(customIndicators);
    },
    context_menu: {
      items_processor: (items, actionsFactory, params) => {
        // Add telemetry option on chart context menus (only when callback is wired)
        // Menu names: "ChartContextMenu" (main chart), "series", "pane"
        const chartMenus = ["ChartContextMenu", "series", "pane"];
        const hasTelemetry = typeof window !== "undefined" && window.showTelemetryModal;

        if (chartMenus.includes(params.menuName) && hasTelemetry) {
          const separator = actionsFactory.createSeparator();
          const telemetryAction = actionsFactory.createAction({
            actionId: "view-telemetry-data" as TV.ActionId,
            label: "📊 View Telemetry Data",
            onExecute: () => {
              window.showTelemetryModal?.();
            },
          });

          // Return new array since items is readonly
          return Promise.resolve([...items, separator, telemetryAction]);
        }

        return Promise.resolve(items);
      },
    },
    ...options, // ref to the container and other props that could include
  };

  return { widgetOptions, datafeed: symbolDatafeed };
};

// Store current datafeed instance for use by utility functions
let currentDatafeed: T.ExternalDatafeed | null = null;

export const setCurrentDatafeed = (datafeed: T.ExternalDatafeed) => {
  currentDatafeed = datafeed;
};

/** The parts of a chart pane that renderStudies reads. */
type StudyPane = Pick<TV.IPaneApi, "getHeight" | "hasMainSeries"> & {
  getLeftPriceScales(): readonly Pick<TV.IPriceScaleApi, "getStudies">[];
  getRightPriceScales(): readonly Pick<TV.IPriceScaleApi, "getStudies">[];
};

/** The parts of the chart API that renderStudies uses. */
export type StudyChart = Pick<
  TV.IChartWidgetApi,
  "createStudy" | "removeEntity" | "setAllPanesHeight"
> & {
  getStudyById(entityId: TV.EntityId): Pick<TV.IStudyApi, "mergeUp" | "changePriceScale">;
  getPanes(): StudyPane[];
};

/** The panel of each pane study renderStudies created and kept, by the study's id. */
type StudyPanels = ReadonlyMap<TV.EntityId, string | null | undefined>;

/**
 * Create the custom studies, put the pane studies of one strategy panel in one pane, and size the panes.
 *
 * Aborting `signal` stops the render: no further pane study is created, no further study is merged, the
 * panes are not sized, and a study whose creation resolves after the abort is removed.
 *
 * The returned promise resolves once every create it started has settled and the render has finished or
 * stopped; failures are logged instead of rejecting it.
 */
export const renderStudies = (chart: StudyChart, signal: AbortSignal): Promise<void> => {
  if (!currentDatafeed) {
    console.error("[renderStudies] No datafeed available");
    return Promise.resolve();
  }

  const indicatorsList = currentDatafeed.props.indicators;
  const isEditable = currentDatafeed.props.editableIndicators ?? false;
  const panelHeightRatios = currentDatafeed.props.panelHeightRatios ?? {};

  // Get band pairs from datafeed state (set in custom_indicators_getter)
  const bandPairs = currentDatafeed.state.bandPairs || [];
  const pairedIndicatorNames = new Set(
    bandPairs.flatMap((p) => [p.upperIndicator.name, p.lowerIndicator.name]),
  );

  const requests: panes.StudyRequest[] = [
    // Band group indicators (with filled areas); the name matches generateBandGroupIndicator's
    ...bandPairs.map((pair) => ({
      name: `${pair.baseName} Bands`,
      inMainChart: pair.position === "chart",
      lock: pair.position === "chart",
      panel: pair.upperIndicator.panel,
    })),
    // Single-line indicators, the ones not part of a band pair
    ...indicatorsList
      .filter((indicator) => !pairedIndicatorNames.has(indicator.name))
      .map((indicator) => ({
        name: indicator.name,
        inMainChart: indicator.position === "chart",
        lock: isEditable ? false : indicator.position === "chart", // unlocked when editable
        panel: indicator.panel,
      })),
  ];
  const [mainChartRequests, paneRequests] = partition(requests, (request) => request.inMainChart);

  // The studies on the price chart are created without waiting on each other or on the pane studies
  const mainChartStudies = Promise.all(
    mainChartRequests.map((request) =>
      createStudy(chart, request).then(
        (id) => {
          if (id && signal.aborted) removeLateStudy(chart, request, id);
        },
        (error) => logFailedCreate(request, error),
      ),
    ),
  );
  const paneStudies = createPaneStudies(chart, panes.orderPaneRequests(paneRequests), signal);

  return Promise.all([mainChartStudies, paneStudies])
    .then(([, studyPanels]) => {
      if (!signal.aborted) sizePanes(chart, studyPanels, panelHeightRatios);
    })
    .catch((error) => console.error("[renderStudies] Could not size the indicator panes", error));
};

const logFailedCreate = (request: panes.StudyRequest, error: unknown) =>
  console.error(`[renderStudies] Failed to create study ${request.name}`, error);

/** Remove a study whose creation resolved after the render was aborted. */
const removeLateStudy = (chart: StudyChart, request: panes.StudyRequest, id: TV.EntityId) => {
  try {
    chart.removeEntity(id);
  } catch (error) {
    console.error(`[renderStudies] Could not remove ${request.name} after the abort`, error);
  }
};

/**
 * Create the pane studies one at a time, in the order given: each is created only after the one before it
 * has settled and, when it joined a host, after mergeUp and changePriceScale were called on it. A study
 * created off the price chart opens a pane below the others. The first study of a pane key that the chart
 * creates is the key's host and keeps its pane; each later one it creates is asked to merge up into the pane
 * above it, which TradingView does only when it can, and to take the host's price scale, so their levels line
 * up. renderStudies gives the studies of one key next to each other. A study whose creation rejects is logged
 * and skipped, and one whose creation resolves with no id is skipped, so the next study of its key can be the
 * host. Once `signal` is aborted, a study whose creation resolves after that is removed, and no further study
 * is created or merged.
 *
 * Resolves with the panel of each study it created and kept, by the study's id.
 */
const createPaneStudies = async (
  chart: StudyChart,
  requests: readonly panes.StudyRequest[],
  signal: AbortSignal,
): Promise<StudyPanels> => {
  const hosts = new Map<string, TV.EntityId>();
  const studyPanels = new Map<TV.EntityId, string | null | undefined>();
  for (const request of requests) {
    let id: TV.EntityId | null = null;
    try {
      id = await createStudy(chart, request);
    } catch (error) {
      logFailedCreate(request, error);
    }
    if (signal.aborted) {
      if (id) removeLateStudy(chart, request, id);
      break;
    }
    if (!id) continue;
    studyPanels.set(id, request.panel);
    const key = panes.paneKey(request);
    const hostId = hosts.get(key);
    if (hostId === undefined) {
      hosts.set(key, id);
      continue;
    }
    try {
      const study = chart.getStudyById(id);
      study.mergeUp();
      study.changePriceScale(hostId);
    } catch (error) {
      console.error(`[renderStudies] Could not merge ${request.name} into its pane`, error);
    }
  }
  return studyPanels;
};

const createStudy = async (
  chart: StudyChart,
  request: panes.StudyRequest,
): Promise<TV.EntityId | null> =>
  chart.createStudy(
    request.name,
    request.inMainChart, // forceOverlay
    request.lock, // lock
    {}, // input parameters
    {
      showLabelsOnPriceScale: !request.inMainChart,
    },
    {}, // style options
  );

/**
 * Set the pane heights in one call. A pane under the price pane gets the ratio declared for the panel
 * panePanel finds for it; one without such a ratio is sized as a pane with no declared ratio.
 */
const sizePanes = (
  chart: StudyChart,
  studyPanels: StudyPanels,
  panelHeightRatios: Record<string, number>,
) => {
  const chartPanes = chart.getPanes();
  if (chartPanes.length < 2 || !chartPanes[0].hasMainSeries()) return;
  const total = chartPanes.reduce((sum, pane) => sum + pane.getHeight(), 0);
  const shares = chartPanes.slice(1).map((pane) => {
    const panel = panePanel(pane, studyPanels);
    return panel ? panelHeightRatios[panel] : undefined;
  });
  chart.setAllPanesHeight(panes.paneHeights(total, shares));
};

/**
 * The panel of a pane, read from the studies on its price scales: their panel when they are all pane studies
 * created for one panel, and none otherwise. A pane with no study on a price scale, or with a study on a price
 * scale that renderStudies did not create, has none.
 */
const panePanel = (pane: StudyPane, studyPanels: StudyPanels) => {
  const panels = uniq(
    [...pane.getLeftPriceScales(), ...pane.getRightPriceScales()]
      .flatMap((scale) => scale.getStudies())
      .map((id) => studyPanels.get(id)),
  );
  return panels.length === 1 ? panels[0] : undefined;
};

export const drawPositionsOnTimeFrame = (
  chart: TV.IChartWidgetApi,
  timeRange: T.TV.VisibleTimeRange, // from, to in seconds
) => {
  if (!currentDatafeed) return;

  // Get the last candle time to limit open positions
  const lastCandleTime = currentDatafeed.props.candles?.times?.[0] || timeRange.to;

  // Two-phase position rendering to avoid TradingView arrow clipping.
  // TradingView clips arrow shapes when the start point is outside loaded candle data,
  // causing long positions to render as tiny arrows (e.g. 19-day position shows as 1-2 days).
  //
  // Phase 1 (temp): When only the end is visible, draw a dotted arrow from the visible
  //   edge to the position's end as a visual indicator.
  // Phase 2 (full): When the start enters the visible range (data is loaded), remove
  //   the temp arrow and draw the real solid arrow at full length.
  currentDatafeed.props.positions.forEach((pos: T.AppPosition) => {
    if (isPositionTrackingEnded(pos)) return;
    if (pos.tv_shape_id) return; // Already fully rendered

    const startUnix = dayjs(pos.start_ts).unix();
    const endUnix = pos.end_ts ? dayjs(pos.end_ts).unix() : timeRange.to;

    // Check if position overlaps with visible range at all
    const overlaps = startUnix <= timeRange.to && endUnix >= timeRange.from;
    if (!overlaps) return;

    const startInRange = startUnix >= timeRange.from;

    if (startInRange) {
      // Full data available - remove temp arrow if exists, create real arrow
      if (pos.tv_temp_shape_id) {
        chart.removeEntity(pos.tv_temp_shape_id);
        pos.tv_temp_shape_id = null;
      }

      const arrow = shapes.getSimplePositionArrow(pos, lastCandleTime);
      const shapeId = chart.createMultipointShape(arrow.points, arrow.options);
      pos.tv_shape_id = shapeId;

      // Create the profit/loss label for closed positions (below candles)
      shapes.getPositionExitLabelPointer(pos, chart);
    } else {
      // Start is outside loaded data - create/update temporary dotted arrow from visible edge.
      // Recreated on scroll so the arrow grows as the user scrolls left toward the position start.
      if (pos.tv_temp_shape_id) {
        chart.removeEntity(pos.tv_temp_shape_id);
      }
      const tempArrow = shapes.getTemporaryPositionArrow(pos, timeRange.from, lastCandleTime);
      const tempId = chart.createMultipointShape(tempArrow.points, tempArrow.options);
      pos.tv_temp_shape_id = tempId;
    }
  });
};

const typedArrayCandleData = (rawCandles: T.TV.Bar[]): T.TypedArrayCandles => {
  if (!rawCandles.length) {
    return {
      times: new Uint32Array(0),
      opens: new Float32Array(0),
      highs: new Float32Array(0),
      lows: new Float32Array(0),
      closes: new Float32Array(0),
      volumes: new Float32Array(0),
    };
  }

  // Keep descending order for binary search efficiency
  const sorted = rawCandles.sort((a, b) => b.time - a.time);
  const times = new Uint32Array(sorted.map((c) => c.time));
  const opens = new Float32Array(sorted.map((c) => c.open));
  const highs = new Float32Array(sorted.map((c) => c.high));
  const lows = new Float32Array(sorted.map((c) => c.low));
  const closes = new Float32Array(sorted.map((c) => c.close));
  const volumes = new Float32Array(sorted.map((c) => c.volume!));

  return {
    times,
    opens,
    highs,
    lows,
    closes,
    volumes,
  };
};

const typedArrayTimesValues = (arr: T.DataTimeValue[]): T.TypedArrayTimesValues => {
  if (!arr?.length) {
    return {
      times: new Uint32Array(0),
      values: new Float32Array(0),
    };
  }

  // Keep descending order for binary search efficiency
  const sorted = arr.sort((a, b) => b.time - a.time);
  const times = new Uint32Array(sorted.map((d) => d.time));
  const values = new Float32Array(sorted.map((d) => d.value));

  return {
    times,
    values,
  };
};

export const preparePropsForDatafeed = (
  props: T.RunResult,
  useApiFallback?: boolean,
  options?: { editableIndicators?: boolean; openOnEntry?: boolean },
): T.ArrayTypedPropsForDatafeed => {
  // Filter out volume and candle indicators - TradingView handles these natively via "getBars"
  // If we ever need custom candle/volume indicators, we need to conditionally do it via ChartDefinition
  const customIndicators = props.indicators.filter((i) => !["candle", "volume"].includes(i.type));

  const typedArrayCustomIndicators = customIndicators
    .filter((i) => i.type === "line")
    .map((i) => {
      if (!Array.isArray(i.data)) {
        throw new Error(`Invalid data format for ${i.name}: not an array`);
      }

      const typedLineIndicator: T.TypedArrayLineIndicator = {
        ...i,
        type: "line",
        data: typedArrayTimesValues(i.data as T.DataTimeValue[]),
      };
      return typedLineIndicator;
    });

  // Filter positions to only those matching the chart symbol (for multi-symbol imports)
  const chartSymbolName = props.symbol?.name;
  const filteredPositions = chartSymbolName
    ? props.positions.filter((pos) => pos.symbol_name === chartSymbolName)
    : props.positions;

  return {
    ...props,
    positions: filteredPositions,
    candles: typedArrayCandleData(props.candles), // "getBars" natively handles
    indicators: typedArrayCustomIndicators, // Only custom line indicators, no candle/volume custom indicators
    marks: {
      opens: [],
      closes: [],
    },
    useApiFallback, // Pass through for API fallback mode when cache unavailable
    editableIndicators: options?.editableIndicators,
    openOnEntry: options?.openOnEntry,
    executionBoundaries: props.executionBoundaries,
  } satisfies T.ArrayTypedPropsForDatafeed;
};

/**
 * Creates a single vertical line marker on the chart
 */
const createBoundaryMarker = (
  chart: T.TV.IChartWidgetApi,
  time: number,
  text: string,
  color: string,
): T.TV.EntityId | null => {
  try {
    return chart.createShape(
      { time },
      {
        shape: "vertical_line",
        lock: true,
        overrides: {
          linecolor: color,
          linewidth: 1,
          linestyle: 2, // dashed
          showLabel: true,
          text,
          textcolor: color,
          fontsize: 11,
        },
      },
    );
  } catch (err) {
    console.error("Failed to create boundary marker:", err);
    return null;
  }
};

/**
 * Draws boundary markers (start/end) when they're in the visible time range.
 * Supports execution group views with per-execution markers.
 */
export const drawBoundaryMarkersOnTimeFrame = (
  chart: T.TV.IChartWidgetApi,
  timeRange: T.TV.VisibleTimeRange,
) => {
  if (!currentDatafeed) return;

  const props = currentDatafeed.props;

  // Initialize boundary markers tracking
  if (!currentDatafeed.state.boundaryMarkers) {
    currentDatafeed.state.boundaryMarkers = {
      startId: null,
      endId: null,
    };
  }
  if (!currentDatafeed.state.groupBoundaryMarkerIds) {
    currentDatafeed.state.groupBoundaryMarkerIds = [];
  }

  // --- Execution Group mode: draw per-execution boundaries ---
  if (props.executionBoundaries?.length) {
    // Track which markers have been drawn by a unique key (exec_id + "start"/"end")
    if (!currentDatafeed.state.groupBoundaryDrawnKeys) {
      currentDatafeed.state.groupBoundaryDrawnKeys = new Set<string>();
    }
    const drawnKeys = currentDatafeed.state.groupBoundaryDrawnKeys as Set<string>;
    const existingIds = currentDatafeed.state.groupBoundaryMarkerIds as T.TV.EntityId[];

    const boundaries = props.executionBoundaries;
    const lastIdx = boundaries.length - 1;
    const MARKER_COLOR = "#888888";

    // Extend visible range with buffer to catch markers just outside view
    const buffer = (timeRange.to - timeRange.from) * 0.1;
    const rangeFrom = timeRange.from - buffer;
    const rangeTo = timeRange.to + buffer;

    boundaries.forEach((boundary, idx) => {
      const isLast = idx === lastIdx;

      // Start marker - only create if in/near visible range and not already drawn
      const startKey = `${boundary.executionId}_start`;
      if (
        !drawnKeys.has(startKey) &&
        boundary.startTime >= rangeFrom &&
        boundary.startTime <= rangeTo
      ) {
        const startId = createBoundaryMarker(
          chart,
          boundary.startTime,
          `Exec #${boundary.executionId} Start`,
          MARKER_COLOR,
        );
        if (startId) {
          existingIds.push(startId);
          drawnKeys.add(startKey);
        }
      }

      // End marker (or "Running" for last execution without end)
      const endKey = `${boundary.executionId}_end`;
      if (!drawnKeys.has(endKey)) {
        if (boundary.endTime && boundary.endTime >= rangeFrom && boundary.endTime <= rangeTo) {
          const endId = createBoundaryMarker(
            chart,
            boundary.endTime,
            `Exec #${boundary.executionId} End`,
            MARKER_COLOR,
          );
          if (endId) {
            existingIds.push(endId);
            drawnKeys.add(endKey);
          }
        } else if (!boundary.endTime && isLast) {
          const nowTime = Math.floor(Date.now() / 1000);
          if (nowTime >= rangeFrom && nowTime <= rangeTo) {
            const nowId = createBoundaryMarker(
              chart,
              nowTime,
              `Exec #${boundary.executionId} (Running)`,
              MARKER_COLOR,
            );
            if (nowId) {
              existingIds.push(nowId);
              drawnKeys.add(endKey);
            }
          }
        }
      }
    });

    return;
  }

  // --- Single run mode: draw overall start/end ---
  let startTime: number | undefined;
  let endTime: number | undefined;

  // Try to get boundaries from indicators first (standard backtests)
  const indicatorWithData = props.indicators?.find(
    (ind) => ind.data && ind.data.times && ind.data.times.length > 0,
  );

  if (indicatorWithData?.data?.times?.length) {
    startTime = indicatorWithData.data.times[indicatorWithData.data.times.length - 1];
    endTime = indicatorWithData.data.times[0];
  } else if (props.positions?.length) {
    const positionTimes = props.positions
      .filter((position) => !isPositionTrackingEnded(position))
      .map((p) => {
        const start = Math.floor(new Date(p.start_ts).getTime() / 1000);
        const end = p.end_ts ? Math.floor(new Date(p.end_ts).getTime() / 1000) : null;
        return { start, end };
      })
      .filter((t) => !isNaN(t.start) && isFinite(t.start));

    if (positionTimes.length > 0) {
      startTime = Math.min(...positionTimes.map((t) => t.start));
      const endTimes = positionTimes
        .map((t) => (t.end !== null && !isNaN(t.end) && isFinite(t.end) ? t.end : t.start))
        .filter((t) => !isNaN(t) && isFinite(t));
      if (endTimes.length > 0) {
        endTime = Math.max(...endTimes);
      }
    }
  }

  if (!startTime || !endTime) return;

  const isBacktest = props.positions?.some((p) => p.backtest_id);
  const runType = isBacktest ? "Backtest" : "Execution";
  const markerColor = "#666666";

  if (
    startTime >= timeRange.from &&
    startTime <= timeRange.to &&
    !currentDatafeed.state.boundaryMarkers.startId
  ) {
    currentDatafeed.state.boundaryMarkers.startId = createBoundaryMarker(
      chart,
      startTime,
      `${runType} Start`,
      markerColor,
    );
  }

  if (
    endTime >= timeRange.from &&
    endTime <= timeRange.to &&
    !currentDatafeed.state.boundaryMarkers.endId
  ) {
    currentDatafeed.state.boundaryMarkers.endId = createBoundaryMarker(
      chart,
      endTime,
      `${runType} End`,
      markerColor,
    );
  }
};
